import React, { useEffect, useState } from "react";

import KnxChapter from "./KnxChapter";
import { KnxChapterItem } from "./KnxChapterItem";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import Device from "../../models/Device";
import { isUiModeDeveloper } from "../../uiMode";
import { ValueLevels } from "../../models/KnxInstanceData";

export function findChapterWithProperty(
  propertyOrComObjId: string,
  chapters: Array<KnxChapterItem>,
): KnxChapterItem | undefined {
  for (const chapter of chapters) {
    if (chapter.propertyList && chapter.propertyList.some((p) => p.a.RefId == propertyOrComObjId)) {
      return chapter;
    }
    if (
      chapter.comObjectList &&
      chapter.comObjectList.some((c) => c.a.RefId == propertyOrComObjId || c.a.Number == propertyOrComObjId)
    ) {
      return chapter;
    }

    if (chapter.subChapters) {
      const result = findChapterWithProperty(propertyOrComObjId, chapter.subChapters);
      if (result) {
        return result;
      }
    }
  }
  return undefined;
}

function firstChapterWithContent(chapters: Array<KnxChapterItem>): KnxChapterItem | undefined {
  for (const chapter of chapters) {
    if (chapter.content) {
      return chapter;
    }
    if (chapter.subChapters) {
      const result = firstChapterWithContent(chapter.subChapters);
      if (result) {
        return result;
      }
    }
  }
  return undefined;
}

function findChapterById(
  chapterId: string,
  chapters: Array<KnxChapterItem>,
  autoExpand: boolean = false,
): KnxChapterItem | undefined {
  if (chapterId == "") {
    return firstChapterWithContent(chapters);
  }

  for (const chapter of chapters) {
    if (chapter.chapterId == chapterId) {
      if (autoExpand) {
        chapter.expanded = true;
      }
      return chapter;
    }
    if (chapter.subChapters) {
      const result = findChapterById(chapterId, chapter.subChapters, autoExpand);
      if (result) {
        if (autoExpand) {
          chapter.expanded = true;
        }
        return result;
      }
    }
  }
  return undefined;
}

function filterEmptyChapters(inputChapters: Array<KnxChapterItem>): Array<KnxChapterItem> {
  const res = new Array<KnxChapterItem>();

  for (const c of inputChapters) {
    if (c.subChapters) {
      c.subChapters = filterEmptyChapters(c.subChapters);
    }

    if (!c.headerData || c.headerData?.hidden != false) {
      if (c.content || c.subChapters?.length > 0) {
        res.push(c);
      }
    }
  }

  return inputChapters;
}

function buildDetailHeaders(
  parentText: string,
  chapters: Array<KnxChapterItem>,
  parentChapter: KnxChapterItem | undefined,
): void {
  chapters.forEach((chapter) => {
    chapter.parentKnxChapter = parentChapter;
    if (chapter.headerData) {
      if (parentText) {
        chapter.detailHeader = parentText + " > " + chapter.headerData.productionHeader;
        if (isUiModeDeveloper()) {
          chapter.detailHeader = chapter.detailHeader + "  " + chapter.chapterId;
        }
      } else chapter.detailHeader = chapter.headerData.productionHeader;
    }

    if (chapter.subChapters) {
      buildDetailHeaders(chapter.detailHeader || "", chapter.subChapters, chapter);
    }

    chapter.raiseOnChange();
  });
}

export default function KnxChapterHierachie(props: {
  device: Device;
  knxChapterItems: Array<KnxChapterItem>;
}): JSX.Element {
  const { device, knxChapterItems } = props;

  const [debugId, setDebugId] = useState<string>("");
  const [debugInstanceId, setDebugInstanceId] = useState<string>("");

  const [filteredKnxChapterItems, setFilteredKnxChapterItems] = useState<Array<KnxChapterItem>>();

  useOnChangeWatcher(device);
  useOnChangeWatcher(device.currentKnxChapter);

  useEffect(() => {
    init();
  }, [knxChapterItems]);

  function init() {
    let filtered = knxChapterItems;
    //////////////////////////////////////////////////////////////////////////////////////////////////

    filterEmptyContent(knxChapterItems);

    filtered = filterEmptyChapters(knxChapterItems);

    filtered = moveupChildrenOfNodeWithoutContent(filtered, undefined);

    buildDetailHeaders("", filtered, undefined);

    setFilteredKnxChapterItems(filtered);

    //
    // keep open chapter stable over reEvaluations
    //
    let knxChapter = findChapterById(device.currentKnxChapterId, filtered, true);
    if (!knxChapter) {
      knxChapter = firstChapterWithContent(filtered);
    }
    if (device.currentKnxChapter.value !== knxChapter) {
      device.currentKnxChapter.value = knxChapter;
    }
  }

  const defaultValue = debugId ? device.knxInstanceData.getValue(debugId, debugInstanceId) || "????" : "????";

  if (!filteredKnxChapterItems) {
    return <div>Loading chapters...</div>;
  }

  return (
    <div
      className="estadaColumn left top h100"
      style={{
        overflowY: "auto",
        paddingTop: "10px",
        // borderLeft: "5px solid var(--panel-background)",
        // borderTop: "5px solid var(--panel-background)",
        // borderBottom: "5px solid var(--panel-background)",
      }}
    >
      {isUiModeDeveloper() && (
        <div
          className="estadaRow left"
          style={{ marginTop: "-10px", width: "100%", padding: "10px", backgroundColor: "var(--panel-background)" }}
        >
          Search:{" "}
          <input
            type="text"
            placeholder="Id / Number"
            data-tooltip-id="tv-tooltip"
            data-tooltip-html={"Search for Property or ComObject by Id. You can also enter an ComObject number."} // no itx
            data-tooltip-place="top-start" // no itx
            data-tooltip-variant="info" // no itx
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                setDebugId(e.currentTarget.value.trim());
                const knxChapter = findChapterWithProperty(e.currentTarget.value.trim(), filteredKnxChapterItems);
                device.currentKnxChapter.value = knxChapter;
              }
            }}
            onBlur={(e) => {
              setDebugId(e.currentTarget.value.trim());
              const knxChapter = findChapterWithProperty(e.target.value.trim(), filteredKnxChapterItems);
              device.currentKnxChapter.value = knxChapter;
            }}
          />
          <input
            type="text"
            placeholder="value" // no itx
            key={defaultValue} // important to reset the field when defaultValue changes
            defaultValue={defaultValue}
            onBlur={(e) => {
              device.knxInstanceData.setValue(ValueLevels.debug, e.target.value, debugId, debugInstanceId, "");
            }}
          />
          <div>instanceId: {debugInstanceId}</div>
        </div>
      )}

      {filteredKnxChapterItems?.map((c) => (
        <KnxChapter
          key={c.id} // no itx
          device={device}
          knxChapterItem={c}
          onClick={(knxChapterItem) => {
            setDebugInstanceId(knxChapterItem.instanceId);
          }}
        />
      ))}
    </div>
  );
}

function moveupChildrenOfNodeWithoutContent(
  inputChapters: KnxChapterItem[],
  parentChapter: KnxChapterItem | undefined,
): KnxChapterItem[] {
  let res = new Array<KnxChapterItem>();

  let i = 1;
  for (const chapter of inputChapters) {
    res = res.concat(moveupChildrenOfNodeWithoutContent1(chapter, parentChapter));
    i++;
  }

  return res;
}

function moveupChildrenOfNodeWithoutContent1(
  inputChapter1: KnxChapterItem,
  parentChapter: KnxChapterItem | undefined,
): KnxChapterItem[] {
  if (!inputChapter1.content && inputChapter1.subChapters.length == 1) {
    const onlyChild = inputChapter1.subChapters[0];

    // move up only if the child has content or is not hidden
    if (onlyChild.headerData?.productionHeader == inputChapter1.headerData?.productionHeader) {
      return moveupChildrenOfNodeWithoutContent1(onlyChild, inputChapter1);
    }
  }

  if (inputChapter1.content || inputChapter1.headerData?.productionHeader || inputChapter1.subChapters.length == 0) {
    return [
      new KnxChapterItem({
        ...inputChapter1,
        subChapters: moveupChildrenOfNodeWithoutContent(inputChapter1.subChapters, inputChapter1),
      }),
    ];
  }

  if (!inputChapter1.content && inputChapter1.subChapters.length == 0) {
    return [];
  }

  const xSubChapters = moveupChildrenOfNodeWithoutContent(inputChapter1.subChapters, inputChapter1);

  return [...xSubChapters];
}

function filterEmptyContent(knxChapterItems: KnxChapterItem[]): void {
  knxChapterItems.forEach((chapter) => {
    if (chapter.subChapters) {
      filterEmptyContent(chapter.subChapters);
    }
    const nonNullContent = chapter.content?.filter((c) => c != null) || [];
    if (nonNullContent.length == 0) {
      chapter.content = undefined;
    }
  });
}
